import React from "react";
import { Link } from "react-router-dom";
import { fmt, fmtDate } from "@/lib/constants";
import { AlertTriangle, Target, PiggyBank, PhoneCall, CreditCard, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

const URGENCY = {
  high: { ring: "border-rose-500/40 bg-rose-500/5", chip: "bg-rose-500/15 text-rose-500 border-rose-500/30", icon: "bg-rose-500/15 text-rose-500", btn: "bg-rose-500 hover:bg-rose-600", label: "Do now" },
  medium: { ring: "border-amber-500/40 bg-amber-500/5", chip: "bg-amber-500/15 text-amber-500 border-amber-500/30", icon: "bg-amber-500/15 text-amber-500", btn: "bg-amber-500 hover:bg-amber-600", label: "Next up" },
  low: { ring: "border-teal-500/40 bg-teal-500/5", chip: "bg-teal-500/15 text-teal-500 border-teal-500/30", icon: "bg-teal-500/15 text-teal-500", btn: "bg-teal-500 hover:bg-teal-600", label: "Smart move" },
  none: { ring: "border-emerald-500/30 bg-emerald-500/5", chip: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30", icon: "bg-emerald-500/15 text-emerald-500", btn: "bg-emerald-500 hover:bg-emerald-600", label: "On track" },
};

function content(a) {
  switch (a.type) {
    case "pay_bill": {
      const overdue = a.days < 0;
      return {
        Icon: AlertTriangle,
        title: overdue ? `${a.name} is overdue` : `${a.name} is due soon`,
        detail: overdue
          ? `Was due ${fmtDate(a.due_date)} — ${Math.abs(a.days)} day${Math.abs(a.days) === 1 ? "" : "s"} ago. Pay ${fmt(a.amount)} now to avoid a late fee.`
          : `Due ${fmtDate(a.due_date)} — in ${a.days} day${a.days === 1 ? "" : "s"}. Pay ${fmt(a.amount)} to stay ahead.`,
      };
    }
    case "pay_target":
      return { Icon: Target, title: `Pay your target: ${a.name}`, detail: `Put ${fmt(a.amount)} toward ${a.name} this month${a.extra > 0 ? ` (${fmt(a.extra)} of it is your snowball)` : ""} — it's the debt your plan is attacking first.` };
    case "sweep_surplus":
      return { Icon: PiggyBank, title: `Sweep ${fmt(a.amount)} of spare cash into debt`, detail: `Your forecast has room every month. Boost your snowball by ${fmt(a.amount)} to be debt-free sooner.` };
    case "lower_apr":
      return { Icon: PhoneCall, title: `Call to lower ${a.name}'s ${a.apr}% APR`, detail: `A 10-minute call could cut your rate — often more than months of extra payments. We've written the script for you.` };
    case "reduce_utilization":
      return { Icon: CreditCard, title: `Bring utilization under ${a.goal}%`, detail: `Your credit usage is ${a.utilization}%. Paying it down toward ${a.goal}% can lift your credit score.` };
    case "setup":
      return { Icon: Sparkles, title: "Add your first card or loan", detail: "Tell OWEverit what you owe and we'll build your fastest payoff plan." };
    default:
      return { Icon: CheckCircle2, title: "You're on track — nothing urgent", detail: a.payoff_date ? `Keep going and you'll be debt-free by ${fmtDate(a.payoff_date)}.` : "Everything's handled for now. Nice work." };
  }
}

export default function DoThisNext({ action }) {
  if (!action) return null;
  const u = URGENCY[action.urgency] || URGENCY.low;
  const { Icon, title, detail } = content(action);
  return (
    <div className={`p-5 rounded-2xl border ${u.ring} flex items-start gap-4`} data-testid="do-this-next">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${u.icon}`}><Icon className="h-5 w-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Do this next</span>
          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${u.chip}`} data-testid="do-this-next-urgency">{u.label}</span>
        </div>
        <div className="font-display font-bold text-lg mt-1" data-testid="do-this-next-title">{title}</div>
        <p className="text-sm text-muted-foreground mt-0.5">{detail}</p>
      </div>
      <Link to={action.cta_link} data-testid="do-this-next-cta"
        className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold transition ${u.btn}`}>
        {action.cta_label} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
